import { SportsGolf } from '@mui/icons-material';
import { BookingWizardFlowTypes } from 'common/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { endOfDay } from 'date-fns';
import { useFormik } from 'formik';
import { decode } from 'html-entities';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { date, number, object, string } from 'yup';

import { formatPricing } from 'application/modules/bookingWizard/common/helpers';
import useCaseChooseATimeForLesson, {
  ChooseATimeForm,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseChooseATimeForLesson';
import ButtonsGroup from 'infrastructure/components/ButtonsGroup';
import HorizontalCard, { HorizontalCardType } from 'infrastructure/components/HorizontalCard';
import NavigationBar from 'infrastructure/components/NavigationBar';
import PageLayout from 'infrastructure/components/PageLayout';
import SectionTitle from 'infrastructure/components/SectionTitle';
import {
  selectExperienceType,
  selectLessonType,
  selectLocation,
  selectReservationDate,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import {
  setClubFittingType,
  setExperienceType,
  setInstructorDetails,
  setReservationDate,
} from 'infrastructure/redux/slices/bookingWizard.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';
import useFirstLessonAvailableDates from 'infrastructure/targets/web/modules/bookingWizard/hooks/useFirstLessonAvailableDates';
import useStaffAvailableDatesClubFittings from 'infrastructure/targets/web/modules/bookingWizard/hooks/useStaffAvailableDatesClubFittings';
import useStaffAvailableDatesSessionType from 'infrastructure/targets/web/modules/bookingWizard/hooks/useStaffAvailableDatesSessionType';
import { ISessionSummary } from 'infrastructure/targets/web/modules/bookingWizard/Steps/SessionLengthStep';
import ReservationSummary from 'infrastructure/targets/web/modules/bookingWizard/Steps/SessionLengthStep/components/ReservationSummary';
import { getLessonBookingImage } from 'infrastructure/targets/web/modules/common/imageUrlHelpers';

import BookFittingWithInstructor from './components/BookFittingWithInstructor';
import BookLessonWithInstructor from './components/BookLessonWithInstructor';
import {
  StyledAsideContainer,
  StyledButtonsContainer,
  StyledContainer,
  StyledLeftContainer,
} from './style';

const ChooseATimeStep = () => {
  const selectedLocationId = useAppSelector(selectLocation);
  const selectedLessonType = useAppSelector(selectLessonType);
  const selectedBookingType = useAppSelector(selectExperienceType);
  const reservationDate = useAppSelector(selectReservationDate);

  const {
    nextStep,
    prevStep,
    selectedInstructor,
    isFirstTimeLesson,
    setStaffAvailabilitiesStartDate,
    setStaffAvailabilitiesEndDate,
    availableFirstTimeLessonSlots,
    getAvailableLessonSlotButtons,
    lessonTypes,
    scheduledLessonTypes,
    inProgress,
    getPricing,
    isPricingLoading,
    pricingError,
    appointentPricing,
    appointmentError,
    areAppointmentsLoading,
    result: firstTimeLessonAppointmentAvailabilities,
    clubFittingTypes,
    checkIfAtLeastOneSlotAvailable,
  } = useCaseChooseATimeForLesson();

  const {
    staffAvailableDatesSessionType,
    setAvailableDatesSessionTypeSessionTypeId,
    fetchFurtherAvailableDates,
  } = useStaffAvailableDatesSessionType({
    staffId: selectedInstructor?.staffId,
    locationId: selectedLocationId,
  });

  // Only use useFirstLessonAvailableDates if isFirstTimeLesson is true
  const firstLessonDatesHook = isFirstTimeLesson
    ? useFirstLessonAvailableDates({ locationId: selectedLocationId })
    : { firstLessonAvailableDates: [], fetchFurtherAvailableDates: () => {} };

  const {
    firstLessonAvailableDates,
    fetchFurtherAvailableDates: fetchFurtherAvailableFirstLessonDates,
  } = firstLessonDatesHook;

  const {
    staffAvailableFittingDates,
    fetchFurtherAvailableDates: fetchFurtherAvailableFittingDates,
  } = useStaffAvailableDatesClubFittings({
    staffId: selectedInstructor?.staffId,
    locationId: selectedLocationId,
  });

  const [sessionSummary, setSessionSummary] = useState<ISessionSummary>();
  const [sessionHour, setSessionHour] = useState<string>('');
  const [sessionLength, setSessionLength] = useState<number | undefined>(undefined);
  const [lessonType, setLessonType] = useState<number | undefined>(undefined);
  const today = useRef(new Date());
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [showStartTime, setShowStartTime] = useState<boolean>(false);
  const [showLessonTypes, setShowLessonTypes] = useState<boolean>(false);
  const [fittingType, setFittingType] = useState<number | undefined>(undefined);
  const [chipLabel, setChipLabel] = useState<string>('');
  const [defaultTimeLength, setDefaultTimeLength] = useState<number>(60);
  const resolveInitialBookingType = () => {
    if (selectedBookingType === BookingWizardFlowTypes.ClubFittingbyCoach) {
      return BookingWizardFlowTypes.ClubFitting;
    }
    if (
      selectedBookingType === BookingWizardFlowTypes.FirstLesson ||
      selectedBookingType === BookingWizardFlowTypes.ClubFitting
    ) {
      return selectedBookingType;
    }
    return BookingWizardFlowTypes.Lesson;
  };
  const [bookingType, setBookingType] = useState<string>(resolveInitialBookingType());
  const [hasAvailableSlotsOnSelectedDate, setHasAvailableSlotsOnSelectedDate] =
    useState<boolean>(false);
  const dispatch = useAppDispatch();
  const formattedAppointments = formatPricing(appointentPricing);
  const staffFullName =
    selectedInstructor?.staffFirstName &&
    selectedInstructor?.staffLastName &&
    `${selectedInstructor?.staffFirstName} ${selectedInstructor?.staffLastName}`;

  const availableLessonSlots = useCallback(() => {
    if (lessonType) {
      return getAvailableLessonSlotButtons(lessonType);
    }
  }, [lessonType, selectedLessonType, areAppointmentsLoading]);

  const handleBookingTypeChange = (value: BookingWizardFlowTypes) => {
    setBookingType(value);
    dispatch(setExperienceType({ experienceType: value }));
    setFittingType(undefined);
    setSessionLength(undefined);
    setLessonType(undefined);
    setSessionHour('unset');
  };

  const validationSchema = useMemo(
    () =>
      object().shape({
        location: string()
          .required()
          .test('empty-check', 'Location cannot be empty', (fieldValue) =>
            fieldValue ? fieldValue?.length >= 0 : false,
          ),
        reservationDate: date().required(),
        sessionHour: string().required(),
        lessonLength: number().required(),
        sessionTypeId: number().required(),
        staffId: number().required(),
        resourceId: number().required(),
      }),
    [sessionHour, lessonType],
  );

  useEffect(() => {
    if (!reservationDate) {
      dispatch(
        setReservationDate({
          reservationDate: `${today.current}`,
        }),
      );
    } else {
      handleDateChange(new Date(reservationDate));
    }
    setShowCalendar(true);
  }, []);

  const chooseATimeForm = useFormik<ChooseATimeForm>({
    initialValues: {
      location: selectedLocationId || '',
      reservationDate: reservationDate || `${today.current}`,
      sessionHour: '',
      lessonLength: selectedLessonType,
      sessionTypeId: undefined,
      resourceId: undefined,
      staffId: undefined,
    },
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    onSubmit: (values) => {
      nextStep(values);
    },
  });

  const handleSessionHourChange = async (value: string, event: MouseEvent<HTMLElement>) => {
    const selectedResourceId = event?.target.dataset.secondaryvalue;
    const selectedResourceIdForFirstTimeLesson = event?.target.dataset.tertiaryvalue;
    setSessionHour(value);
    await chooseATimeForm.setFieldValue('sessionHour', value);

    if (isFirstTimeLesson) {
      const selectedStaffId = event?.target.dataset.additionalvalue;
      await chooseATimeForm.setFieldValue('staffId', selectedStaffId);

      const selectedSlot = firstTimeLessonAppointmentAvailabilities?.find(
        (slot) => slot.time.toString() === value,
      );
      const selectedInstructor = selectedSlot?.availabilities.find(
        (availability) => availability.staffId === Number(selectedStaffId),
      );
      dispatch(setInstructorDetails({ ...selectedInstructor }));
      await chooseATimeForm.setFieldValue(
        'sessionTypeId',
        Number(availableFirstTimeLessonSlots?.find((slot) => slot.value === value)?.secondaryValue),
      );

      await chooseATimeForm.setFieldValue('resourceId', selectedResourceIdForFirstTimeLesson);
    } else {
      await chooseATimeForm.setFieldValue('resourceId', selectedResourceId);
      await chooseATimeForm.setFieldValue('staffId', selectedInstructor?.staffId);
    }
  };

  const handleLessonTypeChange = async (value: string, event: MouseEvent<HTMLElement>) => {
    setShowStartTime(true);
    const selectedSessionTypeId = value;
    const sessionLength = event?.currentTarget.dataset.additionalvalue;
    setLessonType(Number(selectedSessionTypeId));
    setSessionHour('unset');
    setSessionLength(Number(sessionLength));
    await chooseATimeForm.setFieldValue('sessionHour', '');
    await chooseATimeForm.setFieldValue('lessonLength', Number(sessionLength));
    await chooseATimeForm.setFieldValue('sessionTypeId', Number(selectedSessionTypeId));
  };

  const handleFittingTypeChange = async (value: string, event: MouseEvent<HTMLElement>) => {
    const selectedFittingTypeId = value;
    const sessionLength = event?.currentTarget.getAttribute('additional-value');
    setFittingType(Number(selectedFittingTypeId));
    setLessonType(Number(selectedFittingTypeId));
    setSessionHour('unset');
    setSessionLength(Number(sessionLength));
    await chooseATimeForm.setFieldValue('sessionHour', '');
    await chooseATimeForm.setFieldValue('lessonLength', Number(sessionLength));
    await chooseATimeForm.setFieldValue('sessionTypeId', Number(selectedFittingTypeId));
    const selectedFittingType = clubFittingTypes?.find(
      (type) => type.id === Number(selectedFittingTypeId),
    );
    if (selectedFittingType) {
      dispatch(setClubFittingType({ clubFittingType: selectedFittingType }));
    }
  };

  const handleDateChange = (value: Date | null) => {
    if (!value) return;
    if (bookingType === BookingWizardFlowTypes.ClubFitting) setShowStartTime(true);
    setStaffAvailabilitiesStartDate(value);
    setStaffAvailabilitiesEndDate(endOfDay(value));
    setSessionHour('unset');
    chooseATimeForm.setFieldValue('sessionHour', '');
    chooseATimeForm.setTouched({ sessionHour: true });
    dispatch(setReservationDate({ reservationDate: `${value}` }));
    chooseATimeForm.setFieldValue('reservationDate', value);
    chooseATimeForm.setTouched({ reservationDate: true });
  };

  useEffect(() => {
    let isAvailable = false;
    if (bookingType !== BookingWizardFlowTypes.ClubFitting && reservationDate) {
      isAvailable = checkIfAtLeastOneSlotAvailable(new Date(reservationDate));
    }
    setHasAvailableSlotsOnSelectedDate(isAvailable);
    setShowLessonTypes(isAvailable); // Keep setShowLessonTypes in sync
  }, [reservationDate, bookingType, checkIfAtLeastOneSlotAvailable]);

  useEffect(() => {
    if (
      isFirstTimeLesson &&
      firstTimeLessonAppointmentAvailabilities &&
      firstTimeLessonAppointmentAvailabilities.length !== 0
    ) {
      const firstTimeLesson = firstTimeLessonAppointmentAvailabilities[0];
      const { defaultTimeLength: firstLessonLength } = firstTimeLesson;
      setDefaultTimeLength(firstLessonLength);
      setLessonType(Number(firstLessonLength));
      chooseATimeForm.setFieldValue('lessonLength', firstLessonLength);
      setChipLabel(`${firstLessonLength} Minute Lesson`);
      setShowStartTime(true);
    } else if (isFirstTimeLesson) {
      setChipLabel(`${defaultTimeLength} Minute Lesson`);
    } else if (selectedInstructor?.staffLevel) {
      setChipLabel(selectedInstructor?.staffLevel);
    }
  }, [firstTimeLessonAppointmentAvailabilities]);

  useEffect(() => {
    if (scheduledLessonTypes?.length && !isFirstTimeLesson) {
      chooseATimeForm.setFieldValue(
        'sessionTypeId',
        scheduledLessonTypes?.find((lesson) => Number(lesson.value) === selectedLessonType)?.value,
      );
      const halfHourLesson =
        scheduledLessonTypes?.find((lesson) => lesson.additionalValue === 30) ??
        scheduledLessonTypes[0];
      setAvailableDatesSessionTypeSessionTypeId(Number(halfHourLesson.value));
    } else if (lessonType) {
      chooseATimeForm.setFieldValue('sessionTypeId', lessonType);
      setAvailableDatesSessionTypeSessionTypeId(lessonType);
    }
  }, []);

  useEffect(() => {
    setSessionSummary({
      sessionHour: sessionHour === 'unset' ? '' : sessionHour,
      session: `${sessionLength ? sessionLength / 60 : ''}`,
      instructor: staffFullName,
    });
  }, [sessionHour, lessonType, sessionLength, selectedInstructor]);

  useEffect(() => {
    if (
      chooseATimeForm.isValid &&
      chooseATimeForm.values.sessionTypeId &&
      chooseATimeForm.values.sessionHour &&
      chooseATimeForm.values.staffId
    ) {
      getPricing(chooseATimeForm.values);
    }
  }, [
    chooseATimeForm.isValid,
    chooseATimeForm.values.sessionTypeId,
    chooseATimeForm.values.sessionHour,
    chooseATimeForm.values.staffId,
    chooseATimeForm.values.lessonLength,
  ]);

  const getCardContent: string | undefined = useMemo(() => {
    if (isFirstTimeLesson) {
      return (
        `The Swing Evaluation is a 60-minute session designed to discuss your golf history and any frustrations you are currently having with your game. ` +
        `Then, together, we'll work to establish measurable goals. We will introduce you to the TrackMan technology and evaluate your swing based on ` +
        `seeing you hit multiple different shots, before identifying the root cause of your inconsistencies. Most importantly, we will develop a ` +
        `custom improvement plan to get you on the path towards achieving your golfing dreams!`
      );
    } else if (selectedInstructor) {
      return decode(selectedInstructor?.staffBio);
    }
  }, [isFirstTimeLesson, selectedInstructor]);

  return (
    <PageLayout pageTitle={'Select A Start Time'} shouldFadeIn>
      <NavigationBar backButtonCallback={prevStep} />
      <StyledContainer>
        <StyledLeftContainer>
          <HorizontalCard
            variant={HorizontalCardType.Large}
            chipLabel={chipLabel}
            title={isFirstTimeLesson ? 'Swing Evaluation' : staffFullName || ''}
            imageUrl={
              isFirstTimeLesson
                ? getLessonBookingImage()
                : selectedInstructor?.staffImage || getLessonBookingImage()
            }
            content={getCardContent}
          />
          {selectedBookingType !== BookingWizardFlowTypes.FirstLesson && (
            <>
              <SectionTitle title={'Booking Type'} icon={<SportsGolf />} />
              <StyledButtonsContainer>
                <ButtonsGroup
                  buttonList={[
                    { title: 'Lesson', value: BookingWizardFlowTypes.Lesson },
                    { title: 'Club Fitting', value: BookingWizardFlowTypes.ClubFitting },
                  ]}
                  onChange={(value) => handleBookingTypeChange(value)}
                  selectedValue={bookingType}
                  shouldStayInOneRow
                />
              </StyledButtonsContainer>
            </>
          )}
          {bookingType === BookingWizardFlowTypes.ClubFitting ? (
            <BookFittingWithInstructor
              clubFittingTypes={clubFittingTypes}
              fittingType={fittingType}
              calendarLoadingInProgress={inProgress}
              staffAvailableFittingDates={staffAvailableFittingDates}
              fetchFurtherAvailableDates={fetchFurtherAvailableFittingDates}
              handleFittingTypeChange={handleFittingTypeChange}
              reservationDate={reservationDate}
              showStartTime={showStartTime}
              handleDateChange={handleDateChange}
              availableAppointmentsError={appointmentError}
              areAppointmentsLoading={areAppointmentsLoading}
              sessionHour={sessionHour}
              handleSessionHourChange={handleSessionHourChange}
              availableFittingSlotButtons={availableLessonSlots}
            />
          ) : (
            <BookLessonWithInstructor
              showCalendar={showCalendar}
              inProgress={inProgress}
              availableDates={
                isFirstTimeLesson ? firstLessonAvailableDates : staffAvailableDatesSessionType
              }
              reservationDate={reservationDate}
              handleDateChange={handleDateChange}
              fetchFurtherAvailableDates={
                isFirstTimeLesson
                  ? fetchFurtherAvailableFirstLessonDates
                  : fetchFurtherAvailableDates
              }
              isFirstTimeLesson={isFirstTimeLesson}
              lessonTypes={lessonTypes}
              handleLessonTypeChange={handleLessonTypeChange}
              lessonType={lessonType}
              showStartTime={showStartTime}
              showLessonTypes={showLessonTypes}
              availableFirstTimeLessonSlots={availableFirstTimeLessonSlots}
              availableLessonSlots={availableLessonSlots}
              sessionHour={sessionHour}
              appointmentError={appointmentError}
              areAppointmentsLoading={areAppointmentsLoading}
              handleSessionHourChange={handleSessionHourChange}
              hasAvailableSlotsOnSelectedDate={hasAvailableSlotsOnSelectedDate}
            />
          )}
        </StyledLeftContainer>
        <StyledAsideContainer>
          <ReservationSummary
            sessionSummary={sessionSummary}
            isFormValid={chooseATimeForm.isValid}
            onSubmit={() => chooseATimeForm.handleSubmit()}
            pricing={formattedAppointments.appointments}
            showPricing={lessonType !== undefined}
            isLoading={isPricingLoading}
            error={pricingError}
          />
        </StyledAsideContainer>
      </StyledContainer>
    </PageLayout>
  );
};

export default ChooseATimeStep;
